import { useState, useRef } from 'react';
import { Modal } from '../ui';
import { Button, Select, Textarea } from '../ui';
import { useDisputeEvidence } from '../../hooks/useDisputeEvidence';

const DISPUTE_REASONS = [
  { value: 'notAsDescribed', label: 'Item not as described' },
  { value: 'notReceived', label: 'Item not received / winner no-show' },
  { value: 'sellerUnresponsive', label: 'Seller is unresponsive' },
  { value: 'winnerUnresponsive', label: 'Winner is unresponsive' },
  { value: 'damaged', label: 'Item was damaged' },
];

/**
 * Evidence Uploader Component
 *
 * Inline dropzone with thumbnail previews for dispute evidence.
 */
function EvidenceUploader({ evidenceUrls, onUpload, onRemove, uploading, remainingSlots, error }) {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []).filter((f) =>
      f.type.startsWith('image/')
    );
    if (files.length > 0) {
      onUpload(files.slice(0, remainingSlots));
    }
  }

  function handleFileSelect(e) {
    const files = Array.from(e.target.files || []).filter((f) =>
      f.type.startsWith('image/')
    );
    if (files.length > 0) {
      onUpload(files.slice(0, remainingSlots));
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-cream/80">
        Evidence (optional)
      </label>

      {/* Thumbnails */}
      {evidenceUrls.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {evidenceUrls.map((url, idx) => (
            <div key={url} className="relative group">
              <img
                src={url}
                alt={`Evidence ${idx + 1}`}
                className="w-16 h-16 object-cover rounded-lg border border-cream/20"
              />
              <button
                type="button"
                onClick={() => onRemove(url)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-error text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove image"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dropzone */}
      {remainingSlots > 0 && (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
            ${dragOver ? 'border-primary bg-primary/10' : 'border-cream/20 hover:border-cream/40'}
            ${uploading ? 'opacity-50 cursor-wait' : ''}
          `}
        >
          {uploading ? (
            <p className="text-cream/60 text-sm">Uploading...</p>
          ) : (
            <>
              <UploadIcon className="w-6 h-6 mx-auto text-cream/40 mb-2" />
              <p className="text-cream/60 text-sm">
                Drop images here or click to upload
              </p>
              <p className="text-cream/40 text-xs mt-1">
                {remainingSlots} of 5 slots remaining
              </p>
            </>
          )}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {error && <p className="text-error text-sm">{error}</p>}
    </div>
  );
}

/**
 * Modal for raising a new dispute
 */
export default function DisputeModal({ isOpen, onClose, onSubmit, isSubmitting }) {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null);

  const {
    uploadEvidence,
    removeEvidence,
    clearEvidence,
    uploading,
    evidenceUrls,
    error: uploadError,
    remainingSlots,
  } = useDisputeEvidence();

  function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!reason) {
      setError('Please select a reason');
      return;
    }

    if (description.length < 20) {
      setError('Please provide more details (at least 20 characters)');
      return;
    }

    onSubmit(reason, description, evidenceUrls);
  }

  function handleClose() {
    setReason('');
    setDescription('');
    setError(null);
    clearEvidence();
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Report a Problem" dark>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-cream/70 text-sm">
          If you're having an issue with this transaction, please let us know and we'll help resolve it.
        </p>

        <Select
          label="What's the issue?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          options={[
            { value: '', label: 'Select a reason...' },
            ...DISPUTE_REASONS,
          ]}
          required
        />

        <Textarea
          label="Describe the problem"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Please provide details about what happened..."
          rows={4}
          maxLength={1000}
          helperText="Minimum 20 characters"
        />

        <EvidenceUploader
          evidenceUrls={evidenceUrls}
          onUpload={(files) => uploadEvidence(files)}
          onRemove={removeEvidence}
          uploading={uploading}
          remainingSlots={remainingSlots}
          error={uploadError}
        />

        {error && (
          <p className="text-error text-sm">{error}</p>
        )}

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={isSubmitting || uploading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            loading={isSubmitting}
            disabled={!reason || description.length < 20 || uploading}
            className="flex-1"
          >
            Submit Report
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Modal for responding to a dispute
 */
export function DisputeResponseModal({ isOpen, onClose, onSubmit, isSubmitting, dispute }) {
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null);

  const {
    uploadEvidence,
    removeEvidence,
    clearEvidence,
    uploading,
    evidenceUrls,
    error: uploadError,
    remainingSlots,
  } = useDisputeEvidence();

  const reasonLabel = DISPUTE_REASONS.find(r => r.value === dispute?.reason)?.label || dispute?.reason;

  function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (description.length < 20) {
      setError('Please provide more details (at least 20 characters)');
      return;
    }

    onSubmit(description, evidenceUrls);
  }

  function handleClose() {
    setDescription('');
    setError(null);
    clearEvidence();
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Respond to Dispute" dark>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-midnight-lighter rounded-lg p-4 border border-midnight-lighter">
          <p className="text-cream/50 text-xs uppercase tracking-wide mb-1">Reported Issue</p>
          <p className="text-cream font-medium">{reasonLabel}</p>
          <p className="text-cream/70 text-sm mt-2">{dispute?.description}</p>
        </div>

        <Textarea
          label="Your response"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Please provide your side of the story..."
          rows={4}
          maxLength={1000}
          helperText="Minimum 20 characters"
        />

        <EvidenceUploader
          evidenceUrls={evidenceUrls}
          onUpload={(files) => uploadEvidence(files)}
          onRemove={removeEvidence}
          uploading={uploading}
          remainingSlots={remainingSlots}
          error={uploadError}
        />

        {error && (
          <p className="text-error text-sm">{error}</p>
        )}

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={isSubmitting || uploading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={description.length < 20 || uploading}
            className="flex-1"
          >
            Submit Response
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Icons
function UploadIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}
